import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Map, Car, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { CurrencyInput } from "../../components/CurrencyInput";
import { formatISK, parseCurrencyInput } from "../../lib/currency";

const PRICE_TYPES = ["fixed", "per_person", "per_group", "package_capacity"];
const DAYS = [
  { v: 0, label: "Mon" },
  { v: 1, label: "Tue" },
  { v: 2, label: "Wed" },
  { v: 3, label: "Thu" },
  { v: 4, label: "Fri" },
  { v: 5, label: "Sat" },
  { v: 6, label: "Sun" },
];

const fmtIsk = (n) => formatISK(n);
function fmtDuration(mins) {
  const m = Number(mins || 0);
  if (!m) return "—";
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h ? h + "h " : ""}${r ? r + "m" : h ? "" : "0m"}`.trim();
}

export default function AdminTours() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [t, v, d] = await Promise.all([
        api.get("/admin/tours"),
        api.get("/admin/vehicles"),
        api.get("/admin/drivers"),
      ]);
      setRows(Array.isArray(t.data) ? t.data : []);
      setVehicles(Array.isArray(v.data) ? v.data : []);
      setDrivers(Array.isArray(d.data) ? d.data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (t, field) => {
    try {
      await api.put(`/admin/tours/${t.id}`, { [field]: !t[field] });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tour "${t.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/tours/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing && editing.id) {
        await api.put(`/admin/tours/${editing.id}`, form);
        toast.success("Tour updated");
      } else {
        await api.post("/admin/tours", form);
        toast.success("Tour created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const seedStarterTours = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/tours/seed");
      toast.success(data?.message || "Starter tours synced");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div data-testid="admin-tours-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Tours</h1>
          <p className="text-sm text-slate-500">Catalog, pricing, schedule and resource rules for private tours.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={seedStarterTours}
            disabled={seeding}
            data-testid="tr-seed-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <Map size={14} /> {seeding ? "Seeding…" : "Seed starter tours"}
          </button>
          <button
            onClick={() => setEditing({})}
            data-testid="tr-new-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            <Plus size={14} /> New tour
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">Packages</th>
                <th className="px-4 py-3">Duration</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3">On site</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No tours yet</td></tr>}
              {!loading && rows.map((t) => {
                const starting = t.supports_4pack && t.price_4pack != null
                  ? t.price_4pack
                  : t.supports_8pack && t.price_8pack != null
                  ? t.price_8pack
                  : t.base_price;
                return (
                  <tr key={t.id} data-testid={`tr-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Map size={14} className="text-slate-400" />
                        <div>
                          <p className="font-medium text-slate-900">{t.title}</p>
                          <p className="text-xs text-slate-500 font-mono">{t.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular-nums">{fmtIsk(starting)}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="flex flex-wrap gap-1">
                        {t.supports_4pack && <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold"><Car size={10} /> 4pack</span>}
                        {t.supports_8pack && <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold"><Users size={10} /> 8pack</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs tabular-nums">{fmtDuration(t.estimated_display_minutes)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggle(t, "is_active")}
                        data-testid={`tr-toggle-active-${t.id}`}
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
                          t.is_active ? "bg-emerald-100 text-emerald-900 ring-emerald-200" : "bg-slate-100 text-slate-600 ring-slate-200"
                        }`}
                      >{t.is_active ? "Active" : "Disabled"}</button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggle(t, "show_on_website")}
                        data-testid={`tr-toggle-site-${t.id}`}
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
                          t.show_on_website ? "bg-sky-100 text-sky-900 ring-sky-200" : "bg-slate-100 text-slate-600 ring-slate-200"
                        }`}
                      >{t.show_on_website ? "Yes" : "Hidden"}</button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => setEditing(t)}
                        data-testid={`tr-edit-${t.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900 mr-3"
                      ><Pencil size={12} /> Edit</button>
                      <button
                        onClick={() => remove(t)}
                        data-testid={`tr-delete-${t.id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                      ><Trash2 size={12} /> Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <TourEditor
          initial={editing}
          vehicles={vehicles}
          drivers={drivers}
          onCancel={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function TourEditor({ initial, vehicles, drivers, onCancel, onSave, saving }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    title: initial.title || "",
    slug: initial.slug || "",
    short_description: initial.short_description || "",
    full_description: initial.full_description || "",
    is_active: initial.is_active !== undefined ? initial.is_active : true,
    show_on_website: initial.show_on_website !== undefined ? initial.show_on_website : true,
    featured_image: initial.featured_image || "",
    gallery_images: (initial.gallery_images || []).join(", "),
    price_type: initial.price_type || "fixed",
    base_price: initial.base_price ?? 0,
    supports_4pack: initial.supports_4pack !== undefined ? initial.supports_4pack : true,
    supports_8pack: initial.supports_8pack !== undefined ? initial.supports_8pack : true,
    price_4pack: initial.price_4pack ?? "",
    price_8pack: initial.price_8pack ?? "",
    max_passengers: initial.max_passengers ?? 8,
    requires_driver: initial.requires_driver !== undefined ? initial.requires_driver : true,
    allowed_vehicle_ids: initial.allowed_vehicle_ids || [],
    allowed_driver_ids: initial.allowed_driver_ids || [],
    driving_minutes: initial.driving_minutes ?? 0,
    stopping_minutes: initial.stopping_minutes ?? 0,
    buffer_before_minutes: initial.buffer_before_minutes ?? 0,
    buffer_after_minutes: initial.buffer_after_minutes ?? 0,
    earliest_start_time: initial.earliest_start_time || "",
    latest_start_time: initial.latest_start_time || "",
    operating_days: initial.operating_days || [0, 1, 2, 3, 4, 5, 6],
  });

  const estDisplay = useMemo(
    () => Number(form.driving_minutes || 0) + Number(form.stopping_minutes || 0),
    [form.driving_minutes, form.stopping_minutes]
  );
  const blocked = useMemo(
    () => estDisplay + Number(form.buffer_before_minutes || 0) + Number(form.buffer_after_minutes || 0),
    [estDisplay, form.buffer_before_minutes, form.buffer_after_minutes]
  );

  const toggleArray = (key, value) => {
    const curr = new Set(form[key]);
    if (curr.has(value)) curr.delete(value); else curr.add(value);
    setForm({ ...form, [key]: Array.from(curr).sort() });
  };
  const toggleDay = (v) => toggleArray("operating_days", v);

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const gallery = form.gallery_images.split(",").map((s) => s.trim()).filter(Boolean);
    onSave({
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      short_description: form.short_description || null,
      full_description: form.full_description || null,
      is_active: !!form.is_active,
      show_on_website: !!form.show_on_website,
      featured_image: form.featured_image || null,
      gallery_images: gallery,
      price_type: form.price_type,
      base_price: parseCurrencyInput(form.base_price) || 0,
      supports_4pack: !!form.supports_4pack,
      supports_8pack: !!form.supports_8pack,
      price_4pack: form.price_4pack === "" ? null : parseCurrencyInput(form.price_4pack),
      price_8pack: form.price_8pack === "" ? null : parseCurrencyInput(form.price_8pack),
      max_passengers: form.max_passengers === "" ? null : Number(form.max_passengers),
      requires_driver: !!form.requires_driver,
      allowed_vehicle_ids: form.allowed_vehicle_ids.length ? form.allowed_vehicle_ids : null,
      allowed_driver_ids: form.allowed_driver_ids.length ? form.allowed_driver_ids : null,
      driving_minutes: Number(form.driving_minutes) || 0,
      stopping_minutes: Number(form.stopping_minutes) || 0,
      buffer_before_minutes: Number(form.buffer_before_minutes) || 0,
      buffer_after_minutes: Number(form.buffer_after_minutes) || 0,
      earliest_start_time: form.earliest_start_time || null,
      latest_start_time: form.latest_start_time || null,
      operating_days: form.operating_days,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 p-0 sm:p-6"
      onClick={onCancel}
      data-testid="tr-editor-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-t-2xl sm:rounded-xl bg-white p-6 space-y-5 max-h-[94vh] overflow-auto"
      >
        <div className="flex items-start justify-between">
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Map size={18} /> {isEdit ? "Edit tour" : "New tour"}
          </h3>
          <button type="button" onClick={onCancel} aria-label="close" className="text-slate-500"><X size={18} /></button>
        </div>

        <Section title="Basics">
          <Labeled label="Title *">
            <input required data-testid="tr-field-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
          </Labeled>
          <Labeled label="Slug (auto-generated if empty)">
            <input data-testid="tr-field-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono outline-none focus:border-slate-900" />
          </Labeled>
          <Labeled label="Short description">
            <input data-testid="tr-field-short" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
          </Labeled>
          <Labeled label="Full description">
            <textarea rows={3} data-testid="tr-field-full" value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
          </Labeled>
          <div className="grid gap-3 sm:grid-cols-2">
            <Labeled label="Featured image URL">
              <input data-testid="tr-field-image" value={form.featured_image} onChange={(e) => setForm({ ...form, featured_image: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
            </Labeled>
            <Labeled label="Gallery image URLs (comma-separated)">
              <input data-testid="tr-field-gallery" value={form.gallery_images} onChange={(e) => setForm({ ...form, gallery_images: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
            </Labeled>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Toggle label="Active" testid="tr-field-active" checked={form.is_active} onChange={(v) => setForm({ ...form, is_active: v })} />
            <Toggle label="Show on website" testid="tr-field-site" checked={form.show_on_website} onChange={(v) => setForm({ ...form, show_on_website: v })} />
          </div>
        </Section>

        <Section title="Pricing">
          <div className="grid gap-3 sm:grid-cols-2">
            <Labeled label="Price type">
              <select data-testid="tr-field-ptype" value={form.price_type} onChange={(e) => setForm({ ...form, price_type: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900">
                {PRICE_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Labeled>
            <Labeled label="Base price (kr)">
              <CurrencyInput data-testid="tr-field-base" value={form.base_price} onChange={(value) => setForm({ ...form, base_price: value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums outline-none focus:border-slate-900" />
            </Labeled>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Toggle label="Supports 4pack (any car)" testid="tr-field-s4" checked={form.supports_4pack} onChange={(v) => setForm({ ...form, supports_4pack: v })} />
              <Labeled label="Price 4pack (kr)">
                <CurrencyInput data-testid="tr-field-p4" value={form.price_4pack} onChange={(value) => setForm({ ...form, price_4pack: value })} disabled={!form.supports_4pack} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums outline-none focus:border-slate-900 disabled:bg-slate-50" />
              </Labeled>
            </div>
            <div className="space-y-2">
              <Toggle label="Supports 8pack (9-seat van)" testid="tr-field-s8" checked={form.supports_8pack} onChange={(v) => setForm({ ...form, supports_8pack: v })} />
              <Labeled label="Price 8pack (kr)">
                <CurrencyInput data-testid="tr-field-p8" value={form.price_8pack} onChange={(value) => setForm({ ...form, price_8pack: value })} disabled={!form.supports_8pack} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums outline-none focus:border-slate-900 disabled:bg-slate-50" />
              </Labeled>
            </div>
          </div>
        </Section>

        <Section title="Schedule">
          <div className="grid gap-3 sm:grid-cols-4">
            <Labeled label="Driving min">
              <input type="number" min="0" data-testid="tr-field-drive" value={form.driving_minutes} onChange={(e) => setForm({ ...form, driving_minutes: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
            </Labeled>
            <Labeled label="Stopping min">
              <input type="number" min="0" data-testid="tr-field-stop" value={form.stopping_minutes} onChange={(e) => setForm({ ...form, stopping_minutes: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
            </Labeled>
            <Labeled label="Buffer before">
              <input type="number" min="0" data-testid="tr-field-bbefore" value={form.buffer_before_minutes} onChange={(e) => setForm({ ...form, buffer_before_minutes: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
            </Labeled>
            <Labeled label="Buffer after">
              <input type="number" min="0" data-testid="tr-field-bafter" value={form.buffer_after_minutes} onChange={(e) => setForm({ ...form, buffer_after_minutes: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
            </Labeled>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 flex items-center gap-4" data-testid="tr-computed">
            <span>Customer duration: <span className="font-semibold text-slate-900 tabular-nums">{fmtDuration(estDisplay)}</span></span>
            <span className="text-slate-300">·</span>
            <span>Blocked total: <span className="font-semibold text-slate-900 tabular-nums">{fmtDuration(blocked)}</span></span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Labeled label="Earliest start">
              <input type="time" data-testid="tr-field-early" value={form.earliest_start_time} onChange={(e) => setForm({ ...form, earliest_start_time: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
            </Labeled>
            <Labeled label="Latest start">
              <input type="time" data-testid="tr-field-late" value={form.latest_start_time} onChange={(e) => setForm({ ...form, latest_start_time: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
            </Labeled>
          </div>
          <Labeled label="Operating days">
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const on = form.operating_days.includes(d.v);
                return (
                  <button
                    key={d.v}
                    type="button"
                    onClick={() => toggleDay(d.v)}
                    data-testid={`tr-day-${d.v}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                      on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"
                    }`}
                  >{d.label}</button>
                );
              })}
            </div>
          </Labeled>
        </Section>

        <Section title="Resources">
          <Labeled label="Max passengers">
            <input type="number" min="1" data-testid="tr-field-maxpax" value={form.max_passengers} onChange={(e) => setForm({ ...form, max_passengers: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
          </Labeled>
          <Toggle label="Requires driver" testid="tr-field-rd" checked={form.requires_driver} onChange={(v) => setForm({ ...form, requires_driver: v })} />
          <Labeled label="Allowed vehicles (leave empty = all)">
            <MultiSelectList items={vehicles} labelKey="name" selected={form.allowed_vehicle_ids} onToggle={(id) => toggleArray("allowed_vehicle_ids", id)} testid="tr-vehicles" />
          </Labeled>
          <Labeled label="Allowed drivers (leave empty = all)">
            <MultiSelectList items={drivers} labelKey="name" selected={form.allowed_driver_ids} onToggle={(id) => toggleArray("allowed_driver_ids", id)} testid="tr-drivers" />
          </Labeled>
        </Section>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <button type="submit" disabled={saving} data-testid="tr-editor-save"
            className="flex-1 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create tour"}
          </button>
          <button type="button" onClick={onCancel}
            className="flex-1 rounded-md border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">{title}</p>
      {children}
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900" />
      {label}
    </label>
  );
}

function MultiSelectList({ items, labelKey, selected, onToggle, testid }) {
  if (!items || items.length === 0) {
    return <p className="text-xs text-slate-400">No items available</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {items.map((it) => {
        const on = selected.includes(it.id);
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onToggle(it.id)}
            data-testid={`${testid}-${it.id}`}
            className={`rounded-md border px-2.5 py-1 text-xs ${
              on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"
            }`}
          >
            {it[labelKey]}
          </button>
        );
      })}
    </div>
  );
}
